import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { selectSessionAdvisor, selectAdvisorAdminPortalState } from '@/model/selectors';
import {
  resetAdvisorAdminPortal,
  postAdvisorForAdminSearchRequest,
} from '@/model/advisorAdminPortalModel';
import { clearSubscriptionSeats } from '@/model/subscriptionSeatsModel';
import { isWizard } from '@shared/isWizard';

interface UseAdminModeOptions {
  propAdminMode?: boolean;
  propShowAdvisorSearch?: boolean;
}

export function useAdminMode({
  propAdminMode = false,
  propShowAdvisorSearch = false,
}: UseAdminModeOptions = {}) {
  const dispatch = useDispatch();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { selectedAdvisor } = useSelector(selectAdvisorAdminPortalState);
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from query params
  const adminModeParam = searchParams.get('adminMode') === 'true';
  const advisorIdParam = searchParams.get('advisorId');

  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string | null>(advisorIdParam || null);
  const [isAdminModeEnabled, setIsAdminModeEnabled] = useState(propAdminMode || adminModeParam);
  const [hasInitializedFromParams, setHasInitializedFromParams] = useState(false);

  // Check if current user is a wizard
  const isWizardUser = sessionAdvisor && isWizard(sessionAdvisor);

  // Admin mode is active if explicitly enabled via props OR user toggled it on
  const adminModeActive = propAdminMode || isAdminModeEnabled;
  const showSearch = propShowAdvisorSearch || isAdminModeEnabled;

  // Fetch advisor details on mount if advisorId is in query params
  useEffect(() => {
    if (!hasInitializedFromParams && advisorIdParam && !selectedAdvisor) {
      dispatch(postAdvisorForAdminSearchRequest({ advisorId: advisorIdParam }));
      setHasInitializedFromParams(true);
    }
  }, [advisorIdParam, selectedAdvisor, hasInitializedFromParams, dispatch]);

  // Sync query params with state
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    if (isAdminModeEnabled && !propAdminMode) {
      newParams.set('adminMode', 'true');

      if (selectedAdvisorId) {
        newParams.set('advisorId', selectedAdvisorId);
      } else {
        newParams.delete('advisorId');
      }
    } else {
      newParams.delete('adminMode');
      newParams.delete('advisorId');
    }

    // Only update if params actually changed
    const currentParamsStr = searchParams.toString();
    const newParamsStr = newParams.toString();
    if (currentParamsStr !== newParamsStr) {
      setSearchParams(newParams, { replace: true });
    }
  }, [isAdminModeEnabled, selectedAdvisorId, propAdminMode, searchParams, setSearchParams]);

  const handleAdvisorSelected = useCallback((advisor: Advisor) => {
    setSelectedAdvisorId(advisor.advisorId);
  }, []);

  const handleToggleChange = useCallback(
    (checked: boolean) => {
      setIsAdminModeEnabled(checked);
      if (!checked) {
        // Reset when leaving admin mode
        setSelectedAdvisorId(null);
        dispatch(resetAdvisorAdminPortal());
        dispatch(clearSubscriptionSeats());
      }
    },
    [dispatch],
  );

  const handleChangeAdvisor = useCallback(() => {
    // Clear selection and return to search
    setSelectedAdvisorId(null);
    dispatch(resetAdvisorAdminPortal());
    dispatch(clearSubscriptionSeats());
  }, [dispatch]);

  const hasSelectedAdvisor = Boolean(selectedAdvisorId);

  return {
    // State
    isWizardUser,
    isAdminModeEnabled,
    selectedAdvisorId,
    adminModeActive,
    showSearch,
    hasSelectedAdvisor,

    // Handlers
    handleAdvisorSelected,
    handleToggleChange,
    handleChangeAdvisor,
  };
}
